<template>
  <div id="">
    <Card v-loading="loading">
      <div class="profile-jumbo__header">
        <span class="profile-name" data-test="jumbo__display-name">
          {{ displayName }}
        </span>
        <el-link
          style="margin-left: 10px;"
          type="primary"
          v-if="!editingProfile && profileEditable"
          @click="toggleEditingProfile"
          data-test="jumbo__change-pwd-btn">
          {{ $t('profile.editProfileLink') }}
        </el-link>
      </div>

      <div class="profile-basic-info">
        <span class="profile-basic-info__label">{{ $t('profile.titleLabel') }} :</span>
        <span data-test="jumbo__title">
          {{ title }}
        </span>
      </div>
      <div class="profile-basic-info">
        <span class="profile-basic-info__label">{{ $t('profile.passwordLabel') }} :</span>
        <span data-test="jumbo__title">
          *********
        </span>
        <el-link
          style="margin-left: 10px;"
          type="primary"
          v-if="!editingPassword && passwordEditable"
          @click="toggleEditingPassword"
          data-test="jumbo__change-pwd-btn">
          {{ $t('profile.editPasswordLink') }}
        </el-link>
      </div>

      <el-collapse-transition>
        <UserProfileForm
          v-if="editingProfile"
          v-loading="savingProfile"
          :userProfile="profile"
          :roleEditable="roleEditable"
          :titleEditable="titleEditable"
          :lastnameEditable="false"
          :firstnameEditable="false"
          cancelable
          :saveButtonText="$t('commons.saveUpdate')"
          :cancelButtonText="$t('commons.cancelButtonText')"
          @canceled="toggleEditingProfile"
          @onSave="handleProfileChange"
        />
      </el-collapse-transition>
      <el-collapse-transition>
        <UserPasswordForm
          v-loading="savingNewPassword"
          v-if="editingPassword"
          :saveButtonText="$t('commons.saveUpdate')"
          @canceled="toggleEditingPassword"
          @submited="handlePasswordChange"/>

        <div class="jumbo__danger-zone" v-if="deletable">
          <span class="danger-zone__title">Zone dangereuse</span>
          <el-button
            type="danger"
            icon="el-icon-delete"
            @click="confirmAndProceedDeleteUser">
            Supprimer l'utilisateur
          </el-button>
        </div>
      </el-collapse-transition>
    </Card>
  </div>
</template>
<script>
import _ from 'lodash';
import services from 'services';
import { PASSWORD_PATTERN } from '@/mixins/validatePassword';

import Card from '@/components/Card/Card.vue';
import UserPasswordForm from '@/components/Forms/UserPasswordForm.vue';
import UserProfileForm from '@/components/Forms/UserProfileForm.vue';

export default {
  components: {
    Card,
    UserPasswordForm,
    UserProfileForm,
  },
  props: {
    profile: { type: Object },
    passwordEditable: { type: Boolean, default: false },
    profileEditable: { type: Boolean, default: false },
    roleEditable: { type: Boolean, default: false },
    titleEditable: { type: Boolean, default: false },
    deletable: { type: Boolean, default: false },
  },
  data() {
    return {
      editingPassword: false,
      editingProfile: false,
      savingNewPassword: false,
      savingProfile: false,
      loading: false,
    };
  },
  methods: {
    toggleEditingProfile() {
      this.editingProfile = !this.editingProfile;
    },
    toggleEditingPassword() {
      this.editingPassword = !this.editingPassword;
    },
    showSuccessMessage() {
      this.$message.success({ message: this.$t('commons.updateSuccessMessage') });
    },
    showErrorMessage() {
      this.$message.error({ message: this.$t('commons.defaultErrorMessage') });
    },
    reauthenticateAndChangePassword(newPassword) {
      this.$prompt(
        this.$t('resetPasswordForm.requiresRecentLoginPromptMessage'),
        this.$t('resetPasswordForm.requiresRecentLoginPromptTitle'),
        {
          confirmButtonText: this.$t('commons.continueButtonText'),
          cancelButtonText: this.$t('commons.cancelButtonText'),
          inputPattern: PASSWORD_PATTERN,
          inputErrorMessage: this.$t('resetPasswordForm.wrongPasswordFormatMessage'),
          inputType: 'password',
        },
      ).then(async ({ value }) => {
        await this.$store.dispatch('auth/loginWithCredentials', {
          email: this.profile.email,
          password: value,
        });
        return this.handlePasswordChange({ password: newPassword });
      }).catch(() => {
        this.$message.error({ message: this.$t('commons.operationCanceled') });
      });
      this.savingNewPassword = false;
    },
    async handlePasswordChange({ password }) {
      try {
        this.$mixpanel.track('Changement de mot de passe');
        this.savingNewPassword = true;
        await services.authService.changePassword(password);
        this.showSuccessMessage();
      } catch (e) {
        if (_.get(e, 'code') === 'auth/requires-recent-login') {
          return this.reauthenticateAndChangePassword(password);
        }
        this.showErrorMessage();
      }
      this.savingNewPassword = false;
      this.toggleEditingPassword();
      return 'done';
    },
    async handleProfileChange(profile) {
      try {
        this.savingProfile = true;
        await services.userService.updateUser(this.profile.id, profile);
        this.showSuccessMessage();
      } catch {
        this.showErrorMessage();
      }
      this.savingProfile = false;
      this.toggleEditingProfile();
      this.$emit('profileUpdated', { ...this.profile, ...profile });
    },
    async confirmAndProceedDeleteUser() {
      this.$confirm(
        this.$t('removeUserDialog.confirmMessageText'),
        this.$t('removeUserDialog.confirmBoxTitle'),
        {
          confirmButtonText: this.$t('removeUserDialog.confirmBoxButtonContinue'),
          confirmButtonClass: 'el-button el-button--danger',
          cancelButtonText: this.$t('removeUserDialog.confirmBoxButtonCancel'),
          type: 'warning',
        },
      ).then(() => {
        this.handleDeleteUser();
      }).catch(() => {
        this.$message({
          type: 'info',
          message: this.$t('commons.operationCanceled'),
        });
      });
    },
    async handleDeleteUser() {
      try {
        this.loading = true;
        await services.userService.softDeleteUser(
          this.profile.id,
        );
        this.$message.success({ message: this.$t('commons.defaultSuccessMessage') });
        this.loading = false;
        this.$router.push({ name: 'ManageUsers' });
      } catch (e) {
        console.log('error', e);
        this.showErrorMessage();
      }
    },
  },
  computed: {
    displayName() {
      return _.get(this, 'profile.displayName', this.$t('commons.missingInfo'));
    },
    title() {
      return _.get(this, 'profile.title', this.$t('commons.missingInfo'));
    },
  },
};
</script>
<style lang="stylus" scoped>
@import '../../../assets/css/main.styl';
.profile-jumbo__header {
  text-align: center;
}
.profile-name {
  font-size: $main-title-size;
  color: $secondary-color;
  margin-bottom: 14px;
}
.profile-name__label {
  color: $light-color;
  margin-right: 10px;
}

.profile-basic-info {
  font-size: $body-font-size;
  margin-top: 20px;
  margin-bottom: 20px;
  color: $dark-color;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-basic-info__label {
  color: $light-color;
  margin-right: 10px;
  margin-left: 20px;
  &:first-of-type {
    margin-left: 0px
  }
}

.jumbo__danger-zone {
  border-top: 1px solid whitesmoke;
  padding-top: 25px;
}

.danger-zone__title {
  font-size: 16px;
  margin-bottom: 15px;
  margin-top: 15px;
  display: block;
}

// iPad Portrait
@media only screen and (min-width: 48rem) {
  .profile-jumbo__header {
    text-align: left;
  }
  .profile-basic-info {
    justify-content: flex-start;
  }
}
</style>
